import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { format, subDays } from 'date-fns';
import { calculateDateRange } from '../utils/dateRangeUtils';

// Mismo criterio que Analytics20: 7D = últimos 7 días (end ayer)
const getDefaultRange = () => {
  const range = calculateDateRange('7D', 7, '', '');
  return {
    startDate: range?.startDateStr ?? format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: range?.endDateStr ?? format(subDays(new Date(), 1), 'yyyy-MM-dd'),
  };
};

const defaultRange = getDefaultRange();

const AnalyticsFilterContext = createContext(null);

export function useAnalyticsFilter() {
  const ctx = useContext(AnalyticsFilterContext);
  if (!ctx) {
    throw new Error('useAnalyticsFilter debe usarse dentro de AnalyticsFilterProvider');
  }
  return ctx;
}

export function AnalyticsFilterProvider({ children }) {
  const [startDate, setStartDateState] = useState(() => defaultRange.startDate);
  const [endDate, setEndDateState] = useState(() => defaultRange.endDate);
  const [selectedRange, setSelectedRange] = useState('7D'); // Mismo keys que Analytics20: 7D, 14D, ESTE_MES, CUSTOM, etc.
  const [platform, setPlatform] = useState('all');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedConversions, setSelectedConversions] = useState([]); // { platform, id, name } para filtrar CPA/ROAS
  const [campaignsFromTable, setCampaignsFromTable] = useState([]); // Campañas que aparecen en la tabla (mismo origen que "Resultados por campaña")

  const setStartDate = useCallback((value) => {
    setStartDateState(typeof value === 'string' ? value : format(value, 'yyyy-MM-dd'));
  }, []);

  const setEndDate = useCallback((value) => {
    setEndDateState(typeof value === 'string' ? value : format(value, 'yyyy-MM-dd'));
  }, []);

  const setDateRange = useCallback((start, end) => {
    setStartDateState(typeof start === 'string' ? start : format(start, 'yyyy-MM-dd'));
    setEndDateState(typeof end === 'string' ? end : format(end, 'yyyy-MM-dd'));
    setSelectedRange('CUSTOM');
  }, []);

  const value = useMemo(
    () => ({
      startDate,
      endDate,
      setStartDate,
      setEndDate,
      setDateRange,
      selectedRange,
      setSelectedRange,
      platform,
      setPlatform,
      selectedCampaignIds,
      setSelectedCampaignIds,
      selectedConversions,
      setSelectedConversions,
      campaignsFromTable,
      setCampaignsFromTable,
    }),
    [
      startDate,
      endDate,
      setStartDate,
      setEndDate,
      setDateRange,
      selectedRange,
      platform,
      selectedCampaignIds,
      selectedConversions,
      campaignsFromTable,
    ]
  );

  return (
    <AnalyticsFilterContext.Provider value={value}>
      {children}
    </AnalyticsFilterContext.Provider>
  );
}

export default AnalyticsFilterContext;
